import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { LocaleService } from 'src/app/services/locale.service';
import { ResizeService } from 'src/app/services/resize.service';
import { AuthService } from 'src/app/services/auth.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FromScreen } from 'src/app/models/from-screen';
import { DomSanitizer } from '@angular/platform-browser';
import {
  PlainGalleryConfig,
  PlainGalleryStrategy,
  Image,
  CurrentImageConfig,
  DotsConfig,
  PreviewConfig,
  ButtonsStrategy,
  ButtonsConfig,
  DescriptionStrategy,
  AccessibilityConfig,
  ButtonType,
  ModalGalleryService,
  ModalGalleryConfig,
  ModalGalleryRef,
} from '@ks89/angular-modal-gallery';
import { Util } from '../../util';
import { ConfigService } from '../../services/config.service';
import { LibConfig } from '@ks89/angular-modal-gallery/lib/model/lib-config.interface';
import { ActionType } from 'src/app/models/action';
import { LoginDialogComponent } from '../../login/components/login-dialog/login-dialog.component';

@Component({
  selector: 'app-image-gallery',
  templateUrl: './image-gallery.component.html',
  styleUrls: ['./image-gallery.component.scss'],
})
export class ImageGalleryComponent implements OnInit {
  @Input()
  images: string[];

  @Input()
  recordId: string;

  @Input()
  from: FromScreen;

  @Input()
  placeholder = false;

  imageIndex = 0;
  gallery: Image[] = [];

  isMobile: boolean;
  maxItems = 4;

  galleryConfig: PlainGalleryConfig = {
    strategy: PlainGalleryStrategy.CUSTOM,
    layout: {},
  };

  previewConfig: PreviewConfig = {
    visible: true,
    number: 5,
  };

  imageConfig: CurrentImageConfig = {
    loadingConfig: {
      enable: true,
      type: 5,
    },
    description: {
      strategy: DescriptionStrategy.ALWAYS_VISIBLE,
      imageText: '',
      numberSeparator: '/',
    },
  };

  dotsConfig: DotsConfig;
  accessibilityConfig: Partial<AccessibilityConfig>;
  buttonsConfig: ButtonsConfig;

  @ViewChild('imageGallery', { static: false }) imageGallery!: ElementRef;

  constructor(
    localeService: LocaleService,
    resizeService: ResizeService,
    private authService: AuthService,
    private apiService: ApiService,
    private configService: ConfigService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private modalGalleryService: ModalGalleryService
  ) {
    localeService.getString('gallery').subscribe((values) => {
      this.accessibilityConfig = {
        mainPrevImageTitle: values['prev'],
        mainNextImageTitle: values['next'],
        previewScrollPrevTitle: values['prev'],
        previewScrollNextTitle: values['next'],
        mainContainerTitle: '',
        modalGalleryContentTitle: '',
      };

      this.buttonsConfig = {
        visible: true,
        strategy: ButtonsStrategy.CUSTOM,
        buttons: [
          {
            className: 'fullscreen-image',
            type: ButtonType.FULLSCREEN,
            title: values['fullscreen'],
            fontSize: '20px',
          },
          {
            className: 'close-image',
            type: ButtonType.CLOSE,
            title: values['close'],
            fontSize: '20px',
          },
        ],
      };
    });

    this.isMobile = resizeService.isMobile;
    this.dotsConfig = { visible: this.isMobile };
  }

  ngOnInit() {
    if (this.from === 'RESULT_SCREEN') {
      this.maxItems = 1;
    } else {
      this.maxItems = this.isMobile ? 1 : 4;
    }

    this.buildGallery();
  }

  public buildGallery() {
    this.gallery = [];
    if (!this.placeholder) {
      this.images.map((image) => {
        this.apiService.getRestrictedImage(this.recordId, image).subscribe((data) => {
          const imageBlob = window.URL.createObjectURL(data);
          this.gallery.push(
            new Image(this.imageIndex++, {
              img: this.sanitizer.bypassSecurityTrustResourceUrl(imageBlob),
            })
          );
        });
      });
    } else {
      Array.from(Array(this.images.length).keys()).forEach((n) => {
        this.gallery.push(
          new Image(this.imageIndex++, {
            img: `./assets/blurred-thumbnails/blurred${n}.jpg`,
          })
        );
      });
    }
  }

  openGallery(image: Image, imageIndex: number) {
    if (!this.authService.isAuthenticated()) {
      const customLoginUrl = this.configService.get().customLoginUrl;
      if (customLoginUrl) {
        this.authService.getExternalLoginUrl(customLoginUrl).subscribe((url) => {
          window.location.href = url;
        });

        return;
      }

      return this.dialog
        .open(LoginDialogComponent)
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            (window as any).location.reload();
          } else {
            const action = Util.getAction();
            if (action) {
              Util.setAction({
                ...action,
                type: ActionType.NONE,
              });
            } else {
              Util.removeAction();
            }
          }
        });
    }

    const index = this.getSelectedIndex(image, this.gallery);
    this.galleryConfig = Object.assign({}, this.galleryConfig, { layout: {} });
    this.apiService.galleryHit(this.recordId, this.from);
    this.showModalGallery(1, index);
  }

  showModalGallery(id: number, imageIndex: number, images: Image[] = [...this.gallery]): void {
    const libConfig: LibConfig = {
      accessibilityConfig: this.accessibilityConfig,
      plainGalleryConfig: this.galleryConfig,
      currentImageConfig: this.imageConfig,
      dotsConfig: this.dotsConfig,
      previewConfig: this.previewConfig,
      buttonsConfig: this.buttonsConfig,
    } as LibConfig;
    const dialogRef: ModalGalleryRef = this.modalGalleryService.open({
      id,
      images: images,
      currentImage: images[imageIndex],
      libConfig,
    } as ModalGalleryConfig);
  }

  private getSelectedIndex(image: Image, images: Image[]): number {
    return image ? images.indexOf(image) : -1;
  }
}
